import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import { secondsToHours } from './workTimeReportUtils';

/**
 * Compact donut chart drawn with SVG strokeDasharray.
 * Each segment is a separate <circle> with rotation for individual tooltips.
 * Colors are softened via CSS `filter: saturate()` so the chart blends into
 * the glass aesthetic instead of competing with it.
 */
const WorkTimeMiniDonut = ({
  segments,
  size = 88,
  thickness = 10,
  centerLabelKey = 'Total',
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const total = segments.reduce((acc, s) => acc + (s.seconds || 0), 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Soften strong palette colors to fit the muted glass aesthetic.
  // saturate <1 desaturates; opacity <1 blends with surrounding bg.
  const muteFilter = 'saturate(0.7)';

  if (!total) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0,
        }}
      >
        <svg width={size} height={size} aria-hidden style={{ display: 'block' }}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={colors.card}
            strokeWidth={thickness}
            opacity={0.5}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography
            variant="caption"
            sx={{ opacity: 0.5, fontSize: 11, lineHeight: 1 }}
          >
            —
          </Typography>
        </Box>
      </Box>
    );
  }

  let cumulative = 0;
  const filled = segments.filter((s) => (s.seconds || 0) > 0);

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: 'block', filter: muteFilter }}
      >
        {/* Subtle background ring */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={colors.card}
          strokeWidth={thickness}
          opacity={0.5}
        />

        {filled.map((s) => {
          const fraction = s.seconds / total;
          const dashLen = fraction * circumference;
          const rotation = (cumulative / total) * 360 - 90;
          cumulative += s.seconds;

          return (
            <Tooltip
              key={s.key}
              title={`${s.label}: ${secondsToHours(s.seconds)} h`}
              arrow
            >
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={`${dashLen} ${circumference - dashLen}`}
                strokeLinecap="butt"
                transform={`rotate(${rotation} ${center} ${center})`}
                style={{
                  transition: 'stroke-dasharray 0.3s ease',
                  cursor: 'help',
                }}
              />
            </Tooltip>
          );
        })}
      </svg>

      {/* Center label */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: size >= 80 ? 18 : 14,
            lineHeight: 1,
            color: colors.text,
          }}
        >
          {secondsToHours(total, 0)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            opacity: 0.55,
            fontSize: 10,
            lineHeight: 1,
            mt: 0.3,
            textTransform: 'lowercase',
            letterSpacing: 0.5,
          }}
        >
          {t(centerLabelKey)}
        </Typography>
      </Box>
    </Box>
  );
};

export default WorkTimeMiniDonut;
